// Filters client-side over the existing GET /navigation tree rather than a new search endpoint.
interface SearchResult {
    parents: Array<string>;
    name: string;
    url: string;
}

interface SearchIndexEntry {
    parent: string;
    link: string;
}

interface NavigationTree {
    _search?: Record<string, Record<string, SearchIndexEntry>>;
    [key: string]: NavigationTree | number | Record<string, Record<string, SearchIndexEntry>> | undefined;
}

const RESULT_LIMIT = 40;

function escapeHtml(value: string): string {
    const entities: Record<string, string> = {
        "&" : "&amp;",
        "<" : "&lt;",
        ">" : "&gt;",
        "\"": "&quot;",
        "'" : "&#39;",
    };

    return value.replace(/[&<>"']/g, char => entities[char]);
}

/**
 * Walks a tree entry back into a documentation URL. Most entries are Section -> Page, but
 * GraphQL reference pages nest a level deeper for each type/field anchor, which need a "#name"
 * anchor on the page URL instead.
 */
function treeResultUrl(parents: Array<string>, name: string): string {
    if(parents.length <= 1) {
        const group = parents.length === 0 ? "/" + name : "/" + parents[0];
        const article = parents.length === 0 ? "" : "/" + name;

        return ("/documentation" + group + article).replace(/ /g, "-");
    }

    const page = ("/documentation/" + parents[0] + "/" + parents[1]).replace(/ /g, "-");

    return page + "#" + name;
}

function buildIndex(navigation: NavigationTree): Array<SearchResult> {
    const results: Array<SearchResult> = [];

    function walk(node: NavigationTree, parents: Array<string>): void {
        Object.entries(node).forEach(([ key, value ]) => {
            if(key.startsWith("_")) {
                return;
            }

            if(typeof value === "number") {
                results.push({ parents, name: key, url: treeResultUrl(parents, key) });
            } else if(value && typeof value === "object") {
                // A page with a "_link" entry is itself a result, not just its per-field anchors
                if("_link" in value) {
                    results.push({ parents, name: key, url: treeResultUrl(parents, key) });
                }

                walk(value as NavigationTree, [ ...parents, key ]);
            }
        });
    }

    walk(navigation, []);

    if(navigation._search) {
        Object.entries(navigation._search).forEach(([ section, items ]) => {
            Object.entries(items).forEach(([ item, entry ]) => {
                results.push({ parents: [ section, entry.parent ], name: item, url: entry.link });
            });
        });
    }

    return results;
}

function rank(result: SearchResult, needle: string): number {
    const name = result.name.toLowerCase();

    if(name === needle) {
        return 0;
    }

    if(name.startsWith(needle)) {
        return 1;
    }

    if(name.includes(needle)) {
        return 2;
    }

    return 3;
}

export function initSearch(): void {
    const triggerEl = document.querySelector<HTMLButtonElement>("[data-search-trigger]");
    const overlayEl = document.querySelector<HTMLElement>("[data-search-overlay]");
    const inputEl = document.querySelector<HTMLInputElement>("[data-search-input]");
    const resultsListEl = document.querySelector<HTMLElement>("[data-search-results]");

    if(!triggerEl || !overlayEl || !inputEl || !resultsListEl) {
        return;
    }

    // Re-bound to non-null types - TS's flow narrowing above doesn't cross into the nested
    // function declarations below, even though these are `const` and can't become null again.
    const trigger = triggerEl;
    const overlay = overlayEl;
    const input = inputEl;
    const resultsEl = resultsListEl;

    let indexPromise: Promise<Array<SearchResult>> | null = null;
    let indexLoaded = false;
    let currentResults: Array<SearchResult> = [];
    let highlightIndex = -1;

    function getIndex(): Promise<Array<SearchResult>> {
        if(!indexPromise) {
            indexPromise = fetch("/navigation")
                .then(response => response.json() as Promise<NavigationTree>)
                .then(buildIndex)
                .catch(() => [])
                .then(results => {
                    indexLoaded = true;

                    return results;
                });
        }

        return indexPromise;
    }

    function updateHighlight(): void {
        const items = resultsEl.querySelectorAll<HTMLElement>(".search-result");

        items.forEach((item, index) => {
            item.classList.toggle("search-result--highlight", index === highlightIndex);
        });

        if(highlightIndex >= 0) {
            items[highlightIndex]?.scrollIntoView({ block: "nearest" });
        }
    }

    function renderResults(results: Array<SearchResult>): void {
        currentResults = results;
        highlightIndex = results.length ? 0 : -1;

        if(!results.length) {
            resultsEl.innerHTML = "<div class=\"search-panel__empty\">No results found</div>";

            return;
        }

        resultsEl.innerHTML = results.map(result => {
            const breadcrumb = result.parents.length ?
                `<div class="search-result__parents">${escapeHtml(result.parents.join(" › "))}</div>` :
                "";

            return (
                `<a class="search-result" href="${escapeHtml(result.url)}">` +
                breadcrumb +
                `<div class="search-result__name">${escapeHtml(result.name)}</div>` +
                "</a>"
            );
        }).join("");

        updateHighlight();
    }

    function runSearch(value: string): void {
        const needle = value.trim().toLowerCase();

        if(!indexLoaded) {
            currentResults = [];
            highlightIndex = -1;
            resultsEl.innerHTML =
                "<div class=\"search-panel__loading\">" +
                "<span class=\"search-panel__spinner\"></span>Loading search index...</div>";
        }

        void getIndex().then(all => {
            if(!needle) {
                renderResults(all.slice(0, RESULT_LIMIT));

                return;
            }

            const filtered = all
                .filter(result => (result.parents.join(" ") + " " + result.name).toLowerCase().includes(needle))
                .sort((a, b) => rank(a, needle) - rank(b, needle));

            renderResults(filtered.slice(0, RESULT_LIMIT));
        });
    }

    function openSearch(): void {
        overlay.hidden = false;
        input.value = "";
        input.focus();
        runSearch("");
    }

    function closeSearch(): void {
        overlay.hidden = true;
        trigger.focus();
    }

    trigger.addEventListener("click", openSearch);

    overlay.addEventListener("mousedown", e => {
        if(e.target === overlay) {
            closeSearch();
        }
    });

    input.addEventListener("input", () => {
        runSearch(input.value);
    });

    document.addEventListener("keydown", e => {
        if(overlay.hidden) {
            const tag = document.activeElement?.tagName;

            if(e.key === "/" && document.activeElement !== input && tag !== "INPUT" && tag !== "TEXTAREA") {
                e.preventDefault();
                openSearch();
            }

            return;
        }

        if(e.key === "Escape") {
            closeSearch();
        } else if(e.key === "ArrowDown") {
            e.preventDefault();
            highlightIndex = Math.min(currentResults.length - 1, highlightIndex + 1);
            updateHighlight();
        } else if(e.key === "ArrowUp") {
            e.preventDefault();
            highlightIndex = Math.max(0, highlightIndex - 1);
            updateHighlight();
        } else if(e.key === "Enter") {
            const current = resultsEl.querySelector<HTMLAnchorElement>(".search-result--highlight");

            if(current) {
                e.preventDefault();
                window.location.href = current.getAttribute("href") ?? "";
            }
        }
    });
}
